/**
 * menuImport.js
 * Parses and validates the Lusuku Bulk Menu Upload workbook
 * (Lusuku_Bulk_Menu_Upload_Template.xlsx) into a plan the Menu Management
 * drawer can commit through the existing restaurant endpoints.
 *
 * Deliberately pure: no React, no network, no DOM beyond File/ArrayBuffer.
 * Everything here is a dry run — nothing in this module writes anything.
 *
 * WHY THIS SHAPE
 * The workbook ships a blank template (sheets "1. Sections", "2. Menu Items",
 * "3. Option Groups") alongside a filled worked example (sheets "4./5./6.
 * Cakely ..."). An operator may fill either set, so for each of the three
 * kinds we pick whichever matching sheet actually carries the most data rows
 * rather than trusting a fixed name. The chosen sheet names are returned in
 * `sheetsUsed` so the UI can show which ones were read.
 *
 * NEVER SENT TO THE API: commissionRate, commissionAmount, vendorNetPrice,
 * commissionCalculatedAt, commissionRateSource and country. All are derived
 * server-side from the restaurant's negotiated rate in
 * services/restaurantCommissionService.js. price_ugx is the GROSS
 * customer-facing price; the server derives the vendor's net from it.
 */

import * as XLSX from "xlsx";

/** Mirrors PRODUCT_PAYMENT_REQUIREMENT in config/productPaymentPolicy.js. */
export const PAYMENT_METHODS = Object.freeze([
  "NONE",
  "ANY_ONLINE",
  "MOMO",
  "AIRTEL",
  "VISA",
  "ZAMTEL",
]);

/** models/restaurantSectionProductModel.js caps additionalImages at 5. */
export const MAX_ADDITIONAL_IMAGES = 5;

/** Rows carrying this in `notes` are the template's own example row. */
const EXAMPLE_ROW_MARKER = "delete this example row";

const SHEET_KINDS = {
  sections: /sections\s*$/i,
  items: /menu\s*items\s*$/i,
  optionGroups: /option\s*groups\s*$/i,
};

const REQUIRED_HEADERS = {
  sections: ["section_title"],
  items: ["name", "price_ugx"],
  optionGroups: ["group_name", "option_name"],
};

// ── small helpers ───────────────────────────────────────────────────────────

const str = (v) => (v === null || v === undefined ? "" : String(v).trim());

const norm = (v) => str(v).toLowerCase();

/** "28,000" / "UGX 28000" / 28000 -> 28000. Returns null when not a number. */
export const parseMoney = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const cleaned = String(v).replace(/[^0-9.-]/g, "");
  if (!cleaned || cleaned === "-" || cleaned === ".") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

/**
 * Spreadsheet booleans arrive as real booleans, as "TRUE"/"FALSE" text, or as
 * 1/0. Anything unrecognised returns the supplied fallback rather than
 * silently reading as false.
 */
export const parseBool = (v, fallback = true) => {
  if (v === null || v === undefined || v === "") return fallback;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  const s = norm(v);
  if (["true", "yes", "y", "1"].includes(s)) return true;
  if (["false", "no", "n", "0"].includes(s)) return false;
  return fallback;
};

/** "chicken, grilled , lunch" -> ["chicken","grilled","lunch"] */
export const parseList = (v) =>
  str(v)
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);

/** Filenames are matched case-insensitively; operators rename constantly. */
const fileKey = (name) => norm(name);

// ── sheet selection + header detection ──────────────────────────────────────

/**
 * Finds the header row within the first few rows of a sheet. The workbook puts
 * a title banner on row 1 and headers on row 2, but an operator who deletes the
 * banner should not break the import, so we scan instead of hardcoding.
 * Returns { headerRow, headers } or null when no plausible header row exists.
 */
export const findHeaderRow = (rows, kind) => {
  const wanted = REQUIRED_HEADERS[kind] || [];
  const limit = Math.min(rows.length, 8);
  for (let i = 0; i < limit; i += 1) {
    const cells = (rows[i] || []).map(norm);
    const hits = wanted.filter((h) => cells.includes(h)).length;
    if (hits === wanted.length && wanted.length > 0) {
      return { headerRow: i, headers: cells };
    }
  }
  return null;
};

const rowsToObjects = (rows, headers, headerRow) => {
  const out = [];
  for (let i = headerRow + 1; i < rows.length; i += 1) {
    const raw = rows[i] || [];
    if (raw.every((c) => c === null || c === undefined || str(c) === "")) continue;
    const obj = {};
    headers.forEach((h, idx) => {
      if (h) obj[h] = raw[idx];
    });
    obj.__row = i + 1; // 1-based, matches what the operator sees in Excel
    out.push(obj);
  }
  return out;
};

const isExampleRow = (obj) => norm(obj.notes).includes(EXAMPLE_ROW_MARKER);

/**
 * Reads every sheet whose name matches a kind, and returns the one with the
 * most usable rows. Ties break toward the earlier sheet, which keeps the blank
 * template winning when both are empty.
 */
export const pickSheet = (workbook, kind) => {
  const pattern = SHEET_KINDS[kind];
  let best = null;
  workbook.SheetNames.forEach((sheetName) => {
    if (!pattern.test(sheetName)) return;
    const ws = workbook.Sheets[sheetName];
    if (!ws) return;
    const rows = XLSX.utils.sheet_to_json(ws, {
      header: 1,
      blankrows: false,
      defval: null,
    });
    const found = findHeaderRow(rows, kind);
    if (!found) return;
    const objects = rowsToObjects(rows, found.headers, found.headerRow).filter(
      (o) => !isExampleRow(o)
    );
    if (!best || objects.length > best.rows.length) {
      best = { sheetName, rows: objects };
    }
  });
  return best;
};

// ── row readers ─────────────────────────────────────────────────────────────

const readSections = (sheet) => {
  if (!sheet) return [];
  const seen = new Map();
  sheet.rows.forEach((r) => {
    const title = str(r.section_title);
    if (!title) return;
    const key = norm(title);
    if (seen.has(key)) return; // first definition wins
    seen.set(key, {
      title,
      description: str(r.section_description),
      order: parseMoney(r.section_order),
      sourceRow: r.__row,
    });
  });
  return [...seen.values()];
};

/**
 * Option rows collapse into groups by group_name. min/max come from the first
 * row of each group; later rows only contribute their option.
 */
const readOptionGroups = (sheet) => {
  if (!sheet) return [];
  const groups = new Map();
  sheet.rows.forEach((r) => {
    const groupName = str(r.group_name);
    const optionName = str(r.option_name);
    if (!groupName || !optionName) return;
    const key = norm(groupName);
    if (!groups.has(key)) {
      groups.set(key, {
        name: groupName,
        minOptions: parseMoney(r.min_options) ?? 0,
        maxOptions: parseMoney(r.max_options) ?? 0,
        options: [],
        sourceRow: r.__row,
      });
    }
    groups.get(key).options.push({
      name: optionName,
      price: parseMoney(r.option_price_ugx) ?? 0,
      mandatory: parseBool(r.mandatory, false),
      sourceRow: r.__row,
    });
  });
  return [...groups.values()];
};

const readItems = (sheet) => {
  if (!sheet) return [];
  return sheet.rows
    .filter((r) => str(r.name))
    .map((r) => ({
      sourceRow: r.__row,
      name: str(r.name),
      sectionTitle: str(r.section_title),
      description: str(r.description),
      price: parseMoney(r.price_ugx),
      imageFilename: str(r.image_filename),
      cloudinaryUrl: str(r.cloudinary_url),
      directUrl: str(r.image_direct_url),
      additionalFilenames: parseList(r.additional_image_filenames),
      countInStock: parseMoney(r.count_in_stock),
      enabled: parseBool(r.enabled, true),
      tags: parseList(r.tags),
      optionGroupNames: parseList(r.option_group_names),
      requiredPaymentMethod: str(r.required_payment_method).toUpperCase() || "NONE",
      notes: str(r.notes),
    }));
};

// ── validation ──────────────────────────────────────────────────────────────

/**
 * Resolves where a product's cover (or extra angle) will come from.
 * Order of preference:
 *   1. cloudinary_url already filled  -> use as-is, no upload
 *   2. a picked local file whose name matches image_filename -> upload it
 *   3. nothing -> the dish imports without a photo (a warning on the cover,
 *      and the extra angle is simply dropped)
 *
 * image_direct_url is deliberately NOT a source. It is a Google Drive link,
 * which is not a CDN and 403s the moment the file is private or a shortcut.
 * It stays in the sheet purely so a human can eyeball the mapping.
 */
const resolveImage = (filename, cloudinaryUrl, fileIndex) => {
  if (cloudinaryUrl && /^https?:\/\//i.test(cloudinaryUrl)) {
    return { kind: "url", url: cloudinaryUrl, filename };
  }
  if (filename) {
    const file = fileIndex.get(fileKey(filename));
    if (file) return { kind: "file", file, filename };
    return { kind: "missing", filename };
  }
  return { kind: "none", filename: "" };
};

/**
 * @param {object}   parsed       output of parseWorkbook
 * @param {File[]}   imageFiles   the photo files the operator picked
 * @param {object[]} existingSections sections already on the restaurant,
 *                                    as returned by apiFetchRestaurantSections
 */
export const validateImport = (parsed, imageFiles = [], existingSections = []) => {
  const fileIndex = new Map();
  imageFiles.forEach((f) => {
    if (f && f.name) fileIndex.set(fileKey(f.name), f);
  });

  const existingByTitle = new Map();
  // A RE-IMPORT MUST NOT DUPLICATE THE MENU. Sections were already matched by
  // title; dishes were not, so running the same workbook twice doubled every
  // dish. Dishes are matched by section + name (case-insensitive) against the
  // products the sections already carry, and marked "update" rather than
  // "create". The runner then updates in place and keeps the dish's current
  // photos when the row brings none.
  const existingProductByKey = new Map();
  existingSections.forEach((s) => {
    if (!s || !s.title) return;
    existingByTitle.set(norm(s.title), s);
    (Array.isArray(s.products) ? s.products : []).forEach((p) => {
      if (!p || !p._id || !p.name) return;
      const key = `${norm(s.title)}::${norm(p.name)}`;
      if (!existingProductByKey.has(key)) {
        existingProductByKey.set(key, {
          _id: p._id,
          image: p.image || "",
          additionalImages: Array.isArray(p.additionalImages) ? p.additionalImages : [],
        });
      }
    });
  });

  // Sections referenced by items but never defined on the Sections sheet still
  // need creating, otherwise a perfectly good menu fails on a missing header.
  const sectionByTitle = new Map();
  parsed.sections.forEach((s) => sectionByTitle.set(norm(s.title), { ...s }));
  parsed.items.forEach((it) => {
    const key = norm(it.sectionTitle);
    if (key && !sectionByTitle.has(key)) {
      sectionByTitle.set(key, {
        title: it.sectionTitle,
        description: "",
        order: null,
        sourceRow: it.sourceRow,
        inferred: true,
      });
    }
  });

  const sections = [...sectionByTitle.values()].map((s) => {
    const match = existingByTitle.get(norm(s.title));
    return {
      ...s,
      action: match ? "existing" : "create",
      existingId: match ? match._id : null,
    };
  });

  const groupNames = new Set(parsed.optionGroups.map((g) => norm(g.name)));

  const items = parsed.items.map((it) => {
    const errors = [];
    const warnings = [];

    if (!it.name) errors.push("name is required");
    if (!it.sectionTitle) errors.push("section_title is required");

    if (it.price === null) {
      errors.push("price_ugx is required");
    } else if (!(it.price > 0)) {
      errors.push("price_ugx must be greater than 0");
    } else if (!Number.isInteger(it.price)) {
      warnings.push(`price ${it.price} is not a whole number and will be rounded`);
    }

    // A MISSING PHOTO NEVER FAILS A ROW.
    //
    // It used to: a kitchen onboarding from a printed menu with no shoot had
    // every row rejected, so the whole menu had to be typed by hand. The dish
    // is the thing customers order; the photograph can follow. Both cases are
    // warnings the review table shows, the import summary counts, and the menu
    // editor keeps visible afterwards under its "No photo" filter, so nothing
    // is silently lost - a typo'd filename surfaces as "will import without a
    // photo" rather than as a product that quietly never gets one.
    const existing =
      existingProductByKey.get(`${norm(it.sectionTitle)}::${norm(it.name)}`) || null;
    const keepsPhoto = Boolean(existing && existing.image);

    const cover = resolveImage(it.imageFilename, it.cloudinaryUrl, fileIndex);
    if (cover.kind === "missing") {
      warnings.push(
        keepsPhoto
          ? `no picked file named "${it.imageFilename}", so the current photo is kept`
          : `no picked file named "${it.imageFilename}", so this imports without a photo`
      );
    } else if (cover.kind === "none" && !keepsPhoto) {
      warnings.push(
        it.directUrl
          ? "a Drive link is not an image source, so this imports without a photo. Add the photo file or a cloudinary_url"
          : "imports without a photo. Add it later from the menu editor"
      );
    }

    let additional = it.additionalFilenames.map((fn) =>
      resolveImage(fn, "", fileIndex)
    );
    if (additional.length > MAX_ADDITIONAL_IMAGES) {
      warnings.push(
        `${additional.length} extra angles listed; only the first ${MAX_ADDITIONAL_IMAGES} will be used`
      );
      additional = additional.slice(0, MAX_ADDITIONAL_IMAGES);
    }
    additional
      .filter((a) => a.kind === "missing")
      .forEach((a) =>
        warnings.push(`extra angle "${a.filename}" was not picked and will be skipped`)
      );
    additional = additional.filter((a) => a.kind === "file" || a.kind === "url");

    if (!PAYMENT_METHODS.includes(it.requiredPaymentMethod)) {
      errors.push(
        `required_payment_method "${it.requiredPaymentMethod}" is not one of ${PAYMENT_METHODS.join(
          " / "
        )}`
      );
    }

    if (it.countInStock !== null && it.countInStock < 0) {
      errors.push("count_in_stock cannot be negative");
    }

    it.optionGroupNames.forEach((n) => {
      if (!groupNames.has(norm(n))) {
        errors.push(`option group "${n}" is not defined on the Option Groups sheet`);
      }
    });

    return {
      ...it,
      cover,
      additional,
      action: existing ? "update" : "create",
      existingId: existing ? existing._id : null,
      existing,
      errors,
      warnings,
      ok: errors.length === 0,
    };
  });

  // A name repeated inside one section would create two identical products.
  const seenInSection = new Map();
  items.forEach((it) => {
    const key = `${norm(it.sectionTitle)}::${norm(it.name)}`;
    if (seenInSection.has(key)) {
      it.errors.push(`duplicate of row ${seenInSection.get(key)} in the same section`);
      it.ok = false;
    } else {
      seenInSection.set(key, it.sourceRow);
    }
  });

  const uploadCount = items
    .filter((i) => i.ok)
    .reduce(
      (n, i) =>
        n +
        (i.cover.kind === "file" ? 1 : 0) +
        i.additional.filter((a) => a.kind === "file").length,
      0
    );

  return {
    sheetsUsed: parsed.sheetsUsed,
    sections,
    optionGroups: parsed.optionGroups,
    items,
    unusedFiles: [...fileIndex.keys()].filter(
      (k) =>
        !items.some(
          (i) =>
            fileKey(i.imageFilename) === k ||
            i.additionalFilenames.some((f) => fileKey(f) === k)
        )
    ),
    totals: {
      sectionsToCreate: sections.filter((s) => s.action === "create").length,
      sectionsExisting: sections.filter((s) => s.action === "existing").length,
      optionGroups: parsed.optionGroups.length,
      itemsOk: items.filter((i) => i.ok).length,
      itemsFailed: items.filter((i) => !i.ok).length,
      itemsWithWarnings: items.filter((i) => i.ok && i.warnings.length > 0).length,
      // Photoless imports are a headline number, not a footnote: it is how the
      // operator knows how much of the shoot is still outstanding.
      // A dish that keeps the photo it already has is not "without a photo".
      itemsWithoutPhoto: items.filter(
        (i) =>
          i.ok &&
          i.cover.kind !== "file" &&
          i.cover.kind !== "url" &&
          !(i.existing && i.existing.image)
      ).length,
      itemsToCreate: items.filter((i) => i.ok && i.action === "create").length,
      itemsToUpdate: items.filter((i) => i.ok && i.action === "update").length,
      imagesToUpload: uploadCount,
    },
    canCommit: items.some((i) => i.ok),
  };
};

// ── entry point ─────────────────────────────────────────────────────────────

/** Parses the raw workbook bytes. Throws with a readable message on garbage. */
export const parseWorkbook = (arrayBuffer) => {
  let workbook;
  try {
    workbook = XLSX.read(arrayBuffer, { type: "array" });
  } catch (err) {
    throw new Error(
      "That file could not be read as a spreadsheet. Export it as .xlsx and try again."
    );
  }

  const itemsSheet = pickSheet(workbook, "items");
  if (!itemsSheet) {
    throw new Error(
      'No menu items sheet found. The workbook needs a sheet whose name ends in "Menu Items" with name and price_ugx columns.'
    );
  }
  const sectionsSheet = pickSheet(workbook, "sections");
  const optionsSheet = pickSheet(workbook, "optionGroups");

  return {
    sheetsUsed: {
      items: itemsSheet.sheetName,
      sections: sectionsSheet ? sectionsSheet.sheetName : null,
      optionGroups: optionsSheet ? optionsSheet.sheetName : null,
    },
    sections: readSections(sectionsSheet),
    optionGroups: readOptionGroups(optionsSheet),
    items: readItems(itemsSheet),
  };
};

/** Convenience: File -> validated report. */
export const buildImportPlan = async (workbookFile, imageFiles, existingSections) => {
  const buf = await workbookFile.arrayBuffer();
  const parsed = parseWorkbook(buf);
  return validateImport(parsed, imageFiles, existingSections);
};

export default buildImportPlan;
